import { toast } from 'sonner'

import { useEffect, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, PosButton, Segmented, Switch } from '@/components/ui'
import { ProductModel } from '@/http/models'
import { PosModel } from '@/types'

interface NewProductDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  categories: PosModel.Category[]
  /** A code that matched nothing, prefilled as the barcode. */
  barcode?: string
  onCreated: (product: PosModel.Product) => void
}

/** A product made at the till, offline (A9). It starts at zero stock; the sale may take it negative. */
export const NewProductDialog = ({ open, onOpenChange, categories, barcode, onCreated }: NewProductDialogProps) => {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [price, setPrice] = useState('')
  const [priceType, setPriceType] = useState<'fixed' | 'variable'>('fixed')
  const [soldByUnit, setSoldByUnit] = useState(false)
  const [categoryId, setCategoryId] = useState('')
  const [code, setCode] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setName('')
    setPrice('')
    setPriceType('fixed')
    setSoldByUnit(false)
    setCategoryId('')
    setCode(barcode ?? '')
  }, [open, barcode])

  const p = parseFloat(price) || 0
  const valid = name.trim().length >= 2 && (priceType === 'variable' || p > 0)

  const save = async () => {
    if (!valid || saving) return
    setSaving(true)
    const res = await ProductModel.create({
      name: name.trim(),
      price: priceType === 'variable' ? 0 : p,
      price_type: priceType,
      sold_by_unit: soldByUnit,
      category_id: categoryId || undefined,
      barcode: code.trim() || undefined,
    })
    setSaving(false)
    if (!res.success || !res.data) return toast.error(res.error?.message ?? 'Could not save the product')
    await queryClient.invalidateQueries({ queryKey: ['products'] })
    toast.success(`${res.data.name} created; it syncs in the background`)
    onOpenChange(false)
    onCreated(res.data)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), save())}>
        <DialogHeader>
          <DialogTitle>New product</DialogTitle>
          <DialogDescription>Starts with no stock. The PLU is assigned after it syncs.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="np-name">Name</Label>
            <Input id="np-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label>Price</Label>
            <Segmented<'fixed' | 'variable'>
              value={priceType}
              onChange={setPriceType}
              options={[
                { value: 'fixed', label: 'Fixed' },
                { value: 'variable', label: 'Cashier enters' },
              ]}
            />
            {priceType === 'fixed' && (
              <Input inputMode="decimal" aria-label="Price" placeholder="0.00" value={price} onChange={(e) => setPrice(e.target.value.replace(/[^\d.]/g, ''))} />
            )}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="np-code">Barcode</Label>
            <Input id="np-code" placeholder="Scan the pack, or leave blank for an in-store code" onCameraScan={setCode} value={code} onChange={(e) => setCode(e.target.value)} />
          </div>
          {categories.length > 0 && (
            <div className="grid gap-1.5">
              <Label htmlFor="np-category">Category</Label>
              <select
                id="np-category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="h-9 rounded-sm border border-input bg-card px-3 text-sm text-ink"
              >
                <option value="">None</option>
                {categories.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="np-unit">Sold by weight or length</Label>
              <p className="text-xs text-muted-foreground">Asks for a decimal quantity, such as 1.25 kg.</p>
            </div>
            <Switch id="np-unit" checked={soldByUnit} onCheckedChange={setSoldByUnit} />
          </div>
        </div>
        <DialogFooter>
          <PosButton variant="outline" size="touch" onClick={() => onOpenChange(false)}>
            Cancel
          </PosButton>
          <PosButton size="touch" disabled={!valid || saving} onClick={save}>
            Create and add
          </PosButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
